import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { useCallback } from 'react';
import { useControlsState, useGroupContext } from '@shared/components/filter/state';
import {
  FilterDateSelection,
  FilterNumberSelection,
  FilterPersonSelection,
  FilterRelatedCardSelection,
  FilterSelectionState,
  FilterTagSelection,
  FilterTextSelection,
} from '@shared/components/filter/types';
import { getRelativeDateFilters } from '@shared/data/cards/CardsEntityService';
import {
  isDateComponent,
  isNumberComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { useCurrentUserId } from '@shared/util/hooks';

type IsCardInFilterGroupHandler = (entity: API.WorkflowEntity) => boolean;

export function useCardIsInFilterGroup(): IsCardInFilterGroupHandler {
  const currentUserId = useCurrentUserId();
  const group = useGroupContext();
  const { filters } = useControlsState();

  return useCallback(
    (entity: API.WorkflowEntity) => {
      const allFilters = [
        ...Array.from(group?.values() || []),
        ...Array.from(filters?.values() || []),
      ];
      return getCardIsInFilterGroup({ entity, filters: allFilters, currentUserId });
    },
    [group, filters, currentUserId],
  );
}

interface GetCardIsInFilterGroupProps {
  entity: API.WorkflowEntity;
  filters: FilterSelectionState[];
  currentUserId?: number;
}

export function getCardIsInFilterGroup({
  entity,
  filters,
  currentUserId,
}: GetCardIsInFilterGroupProps): boolean {
  const cardIsInGroup = filters.every(filterOption => {
    const filterComponent = filterOption.filter?.component;
    if (isNil(filterComponent)) {
      return false;
    }

    const fieldValue = entity.fields?.[filterComponent.id];

    if (isNumberComponent(filterComponent)) {
      const numberSelection = filterOption.selection as FilterNumberSelection;
      const selectedValue = numberSelection.value;
      const selectedOperator = numberSelection.operator;

      switch (selectedOperator) {
        case 'empty':
          return isNil(fieldValue);
        case 'EQ':
          return fieldValue === selectedValue;
        case 'GT':
          return fieldValue > selectedValue;
        case 'GTE':
          return fieldValue >= selectedValue;
        case 'LT':
          return fieldValue < selectedValue;
        case 'LTE':
          return fieldValue <= selectedValue;
      }
    }

    if (isDateComponent(filterComponent)) {
      const dateSelection = filterOption.selection as FilterDateSelection;
      const selectedAfter = dateSelection.after;
      const selectedBefore = dateSelection.before;
      const isRelativeBound =
        !!dateSelection.isRelative &&
        (dateSelection.operator === 'startsOn' || dateSelection.operator === 'endsOn');

      if (dateSelection.operator === 'empty') {
        return isNil(fieldValue);
      }

      // A card with no date cannot match any date-range operator.
      // Guard here to avoid moment(undefined) silently returning "now",
      // which would incorrectly match cards with no date against the
      // current week's range.
      if (isNil(fieldValue)) {
        return false;
      }
      if (dateSelection.operator === 'today') {
        return moment(fieldValue).isSame(new Date(), 'day');
      }
      if (dateSelection.operator === 'exactly') {
        return moment(fieldValue).isSame(selectedAfter, 'day');
      }
      if (isRelativeBound) {
        // Resolve the single relative bound to an absolute date against today.
        // `duration` is already signed; startsOn produces `after`, endsOn `before`.
        const relativeDateProps = getRelativeDateFilters({
          isRelative: true,
          comparisonOperator: dateSelection?.comparisonOperator,
          duration: dateSelection?.duration,
          convertToDateRange: true,
        });
        const { after, before } = relativeDateProps.dateRange ?? {};
        if (!isNil(after)) {
          return moment(fieldValue).isSameOrAfter(after);
        }
        if (!isNil(before)) {
          return moment(fieldValue).isSameOrBefore(before);
        }
        return false;
      }
      if (!isNil(selectedAfter) && isNil(selectedBefore)) {
        return moment(fieldValue).isSameOrAfter(selectedAfter);
      }

      if (isNil(selectedAfter) && !isNil(selectedBefore)) {
        return moment(fieldValue).isSameOrBefore(selectedBefore);
      }

      return moment(fieldValue).isBetween(selectedAfter, selectedBefore, undefined, '[]');
    }

    if (isTextComponent(filterComponent)) {
      const { operator, value } = filterOption.selection as FilterTextSelection;
      switch (operator) {
        case 'empty':
          return isEmpty(fieldValue);
        default:
          return !!fieldValue?.toLowerCase().includes(value?.toLowerCase());
      }
    }

    if (isTagComponent(filterComponent)) {
      const tagSelection = filterOption.selection as FilterTagSelection[];
      const selectedValues = tagSelection.map(s => s.value);
      return selectedValues.some(selectedValue => {
        switch (selectedValue) {
          case 'empty':
            return isEmpty(fieldValue);
          default:
            return !!fieldValue?.includes(selectedValue);
        }
      });
    }

    if (isRelatedCardComponent(filterComponent)) {
      const relatedCardSelection = filterOption.selection as FilterRelatedCardSelection[];
      const selectedValues = relatedCardSelection.map(s => s.value);
      return selectedValues.some(selectedValue => {
        switch (selectedValue) {
          case 'empty':
            return isEmpty(fieldValue);
          default:
            return !!fieldValue?.find((item: any) => item.id === selectedValue);
        }
      });
    }

    if (isPersonComponent(filterComponent)) {
      const personSelection = filterOption.selection as FilterPersonSelection[];
      const selectedValues = personSelection.map(s => s.value);
      return selectedValues.some(selectedValue => {
        switch (selectedValue) {
          case 'empty':
            return isEmpty(fieldValue);
          case 'currentUser':
            return !!fieldValue?.includes(currentUserId);
          default:
            return !!fieldValue?.includes(selectedValue);
        }
      });
    }

    return true;
  });

  return cardIsInGroup;
}
